import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { API_CONFIG } from "../../../core/config";
import { notificationApi } from "./notificationApi";
import store from "../../../core/data/redux/store"; // Import Redux store

// Direct file upload function that bypasses RTK Query
export const uploadNotificationFile = async (
  id: string,
  file: File,
  fileType: string
): Promise<ApiResponseType<{ url: string }>> => {
  // Get the auth token from localStorage
  const token = localStorage.getItem("token");

  // Get base URL from API_CONFIG
  const baseUrl = API_CONFIG.BASE_URL;

  // Updated URL structure
  const url = `${baseUrl}notifications/upload/${id}`;

  console.log("Uploading file to URL:", url);

  // Create form data for the file
  const formData = new FormData();
  formData.append("file", file);

  try {
    // Make a direct fetch request
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      body: formData,
    });

    // Parse the response
    const data = await response.json();
    console.log("Upload response:", data);

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  } finally {
    // Use store.dispatch to properly trigger cache invalidation
    store.dispatch(
      notificationApi.util.invalidateTags([
        "Notifications",
        { type: "Notifications", id },
      ])
    );
  }
};

// Direct file download function with updated endpoint
export const downloadFile = async (
  filename: string,
  module = "notifications",
  fileType = "document" // Add fileType parameter with default value
): Promise<void> => {
  try {

    // Get the auth token from localStorage
    const token = localStorage.getItem("token");

    // Get base URL from API_CONFIG
    const baseUrl = API_CONFIG.BASE_URL;

    // Use the correct download API endpoint - updated to match your specific API path
    const url = `${baseUrl}notifications/download/${filename}`;

    console.log("Downloading file from URL:", url);

    // Make the fetch request
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
      },
    });

    if (!response.ok) {
      // Try to parse error response
      try {
        const errorData = await response.json();
        console.error("Download error:", errorData);
        throw {
          status: response.status,
          message: errorData.message || "Failed to download file",
        };
      } catch (e) {
        throw {
          status: response.status,
          message: "Failed to download file",
        };
      }
    }

    // Get file from response
    const blob = await response.blob();

    // Create download URL
    const downloadUrl = window.URL.createObjectURL(blob);

    // Get filename from content-disposition header or use passed filename
    const contentDisposition = response.headers.get("content-disposition");
    let downloadFilename = filename;

    if (contentDisposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches && matches[1]) {
        downloadFilename = matches[1].replace(/['"]/g, "");
      }
    }

    // Create temporary link and trigger download
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = downloadFilename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);

    console.log("File downloaded successfully:", downloadFilename);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

// Update the RTK Query implementation with the new endpoint
export const fileUploadApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadFile: builder.mutation<
      ApiResponseType<{ url: string }>,
      { id: string; file: File; fileType: string; module: string }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          // Updated URL structure
          url: `notifications/upload/${id}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Notifications"],
    }),

    // Fixed RTK Query version of download function with updated endpoint
    downloadFile: builder.mutation<
      void,
      { filename: string; module?: string; fileType?: string }
    >({
      queryFn: async (
        { filename, module = "notifications", fileType = "document" },
        _api,
        _extraOptions,
        _baseQuery
      ) => {
        try {
          // Use the direct download function with updated endpoint
          await downloadFile(filename, module, fileType);
          // Return properly typed response
          return { data: undefined };
        } catch (error) {
          // Properly type the error as FetchBaseQueryError
          return {
            error: {
              status:
                typeof error === "object" && error !== null && "status" in error
                  ? (error.status as string | number)
                  : "CUSTOM_ERROR",
              data: error,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
});

// Change to use the mutation pattern which is more suitable for downloads
export const {
  useUploadFileMutation,
  useDownloadFileMutation, // Changed from useDownloadFileQuery
} = fileUploadApi;
